import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ViewChildren, OnChanges, SimpleChanges, ElementRef, OnDestroy, ViewEncapsulation, Optional, Self, DoCheck, QueryList } from '@angular/core';
import { ControlValueAccessor, FormControl, Validators, AbstractControl, ValidationErrors, NgControl, NgForm, FormGroupDirective } from '@angular/forms';
import { Observable } from 'rxjs';
import { startWith, map, debounceTime } from 'rxjs/operators';

import { COMMA, ENTER, TAB } from '@angular/cdk/keycodes';
import { MatAutocompleteSelectedEvent, MatAutocomplete, MatAutocompleteTrigger, MatChipList, ErrorStateMatcher, CanUpdateErrorState, MatOption } from '@angular/material';

import { isNullOrEmpty } from '@awp/core/utils';
import { _AwpComponentMixinBase } from '../custom-base.component';

export enum DestinationType {
  Region = 1,
  Country = 2,
  Location = 3
}

export interface Destination {
  id: string;
  name: string;
  isPopular: boolean;
  sortOrder: number;
  countryId?: string;
  countryName?: string;
  regionId?: string;
  regionName?: string;
  destinationType: DestinationType;
  searchName?: string;
}

@Component({
  selector: 'ap-destination-select',
  templateUrl: './destination-select.component.html',
  host: {
    'class': 'ap-destination-select',
  },
  encapsulation: ViewEncapsulation.None
})
export class DestinationSelectComponent extends _AwpComponentMixinBase implements OnInit, OnChanges,
    DoCheck, ControlValueAccessor, OnDestroy, CanUpdateErrorState {

  constructor(
    @Optional() @Self() ngControl: NgControl,
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    _defaultErrorStateMatcher: ErrorStateMatcher) {
    super(_defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl);

    if (this.ngControl != null) { // Do this instead of NG_VALUE_ACCESSOR in providers above.
      this.ngControl.valueAccessor = this;
    }

    this.errorStateMatcher = {
      isErrorState: (control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean => {
        let oldState = this.chipList != null ? this.chipList.errorState : undefined;
        let newState = _defaultErrorStateMatcher.isErrorState(control, form);

        newState = newState || (this.formSearchControl != null
          && this.formValueControl != null
          && this.formSearchControl.touched
          && this.formValueControl.invalid);

        if (this.chipList != null && oldState !== newState) {
          this.chipList.errorState = newState;
          this.chipList.stateChanges.next(); // Need to ensure changeDetection is run inside the chiplist component.
        }
        return newState;
      }
    }
  }

  separatorKeysCodes: number[] = [ENTER, COMMA];
  popularList: Destination[] = [];
  places: Observable<Destination[]>;

  formSearchControl = new FormControl();
  formValueControl = new FormControl(null, { validators: [this.validateRestrictedRegions.bind(this)] } );

  isFilterActive = true;
  //values: Destination[] = null;
  DestinationType = DestinationType;

  @Input() destinations: Destination[] = [];
  @Input() selected: Destination[] = [];

  @Input() placeholder: string;
  @Input() label: string;
  @Input() headerText: string;
  @Input() disabled = false;
  @Input() required = false;
  @Input() noMatchLabel = 'No matches found';
  @Input() restrictOriginRegionId: string;
  @Input() validationMessages: { [key: string]: string } = {};

  @Output() selectionChanged = new EventEmitter<Destination[]>();

  @ViewChild('auto', { static: true }) matAutocomplete: MatAutocomplete;
  @ViewChild('input', { static: true }) input: ElementRef<HTMLInputElement>;
  @ViewChild('input', { read: MatAutocompleteTrigger, static: true }) matAutoCompleteTrigger: MatAutocompleteTrigger;
  @ViewChild('chipList', { static: true }) chipList: MatChipList;
  @ViewChildren(MatOption) matOptions: QueryList<MatOption>;

  //#endregion

  // Allow click to toggle autocomplete panel, except on focus
  private canToggle = false;

  ngOnInit() {
    this.places = this.formSearchControl.valueChanges.pipe(
      startWith(null),
      debounceTime(300),
      map((value: string | null) => {
        if (!isNullOrEmpty(value)) {
          this.isFilterActive = true;
          return this.applyFilter(value).sort(this.sortList);
        } else {
          this.isFilterActive = false;
          return this.applyPopularFilter().sort(this.sortList);
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.destinations && changes.destinations.currentValue) {
      this.popularList = this.buildPopularList(changes.destinations.currentValue);
      this.formSearchControl.updateValueAndValidity();
    }
    if (changes.selected && changes.selected) {
      this.writeValue(changes.selected.currentValue);
    }
    if (changes.required) {
      if (changes.required.currentValue === true) {
        this.formValueControl.setValidators([Validators.required, this.validateRestrictedRegions.bind(this)]);
      } else {
        this.formValueControl.setValidators([this.validateRestrictedRegions.bind(this)]);
      }
      this.formValueControl.updateValueAndValidity();
    }
  }

  ngOnDestroy(): void { }

  ngDoCheck(): void {
    if (this.ngControl != null) {
      this.updateErrorState();
    }
  }

  onChange(value: Destination[]) {
    const emittedValue = value != null && value.length > 0 ? [...value] : null;
    this.selectionChanged.emit(emittedValue);
    this._onChange(emittedValue);
    this._onTouched();
  }

  //#region ControlValueAccessor

  _onTouched = () => { };
  _onChange = (value: any) => {};

  writeValue(value: Destination[]): void {
    this.formValueControl.setValue(value != null ? [...value] : []);
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.formSearchControl.disable();
    } else {
      this.formSearchControl.enable();
    }
  }

  //#endregion

  validateRestrictedRegions(control: AbstractControl): ValidationErrors | null {
    if (isNullOrEmpty(this.restrictOriginRegionId)
      || this.formValueControl.value == null
      || this.formValueControl.value.length === 0) {
      return null
    };
    if (window.location.href.indexOf("kiwibank") > -1) return null;

    if (this.formValueControl.value
        .filter(p => p.regionId == this.restrictOriginRegionId)
        .length === this.formValueControl.value.length) {
      return { regionInvalid: true }
    }

    return null;
  }

  onBlur(): void {
    this.canToggle = false;
  }

  toggle(): void {
    if (this.canToggle) {
      if (this.matAutoCompleteTrigger.panelOpen) {
        this.matAutoCompleteTrigger.closePanel();
      } else {
        this.matAutoCompleteTrigger.openPanel();
      }
    } else {
      this.canToggle = true;
    }
  }

  remove(item: Destination): void {
    const value = [...this.formValueControl.value];

    const index = value.indexOf(item);
    if (index >= 0) {
      value.splice(index, 1);
    }
    this.writeValue(value);
    this.onChange(value);
    this.formSearchControl.updateValueAndValidity();
    if(value.length == 0)
      this.input.nativeElement.placeholder = this.placeholder;
  }

  optionSelected(event: MatAutocompleteSelectedEvent): void {
    this.canToggle = true;
    let notifyOnChange = false;

    if (!this.formValueControl.value.includes(event.option.value)) {
      const value = [...this.formValueControl.value];
      value.push(event.option.value);
      this.writeValue(value);
      notifyOnChange = true;
      this.chipList.errorState = false;
    }

    this.input.nativeElement.value = '';
    this.input.nativeElement.placeholder="";
    this.formSearchControl.setValue(null);

    if (notifyOnChange) {
      this.onChange(this.formValueControl.value);
    }
  }

  add(autoComplete: MatAutocomplete): void {
    if (!this.matAutocomplete.isOpen && this.isFilterActive && this.matOptions != null) {
      const selected = this.matOptions.find(p => p.selected);
      if (selected != null && !selected.disabled) {
        const event = new MatAutocompleteSelectedEvent(autoComplete, selected);
        this.optionSelected(event);
      }
    }
  }

  private getSelectedIds(): string[] {
    return this.formValueControl.value != null ? this.formValueControl.value.map(p => p.id) : [];
  }

  private applyFilter(value: string): Destination[] {
    const filterValue = value.toLowerCase();
    const selectedIds = this.getSelectedIds();
    return this.destinations.filter(p => !selectedIds.includes(p.id) // Remove current selection
                                    && p.destinationType !== DestinationType.Region // exclude regions
                                    && p.searchName.indexOf(filterValue) === 0);
  }

  private applyPopularFilter(): Destination[] {
    if (this.popularList == null || this.popularList.length === 0) return [];
    const selectedIds = this.getSelectedIds();

    let result = this.popularList.filter(p => !selectedIds.includes(p.id)); // Remove current selection
    const regions = new Set(result.map(p => p.destinationType !== DestinationType.Region ? p.regionId : null).filter(p => p != null)); // Get Regions

    // filter regions that do not have any countries
    result = result.filter(p => {
      if (p.destinationType === DestinationType.Region) {
        return regions.has(p.id);
      }
      return true;
    });
    return result;
  }

  private buildPopularList(values: Destination[]): Destination[] {
    const clonedList = values.map(p => ({...p}));
    const regionList = clonedList.filter(p => p.destinationType === DestinationType.Region);

    regionList.forEach(region => {
      region.sortOrder = region.sortOrder * 1000;
      let index = region.sortOrder + 1;

      const countryList = clonedList.filter(p => p.regionId === region.id).sort((a,b) => a.searchName.localeCompare(b.searchName));
      countryList.forEach(country => {
        country.sortOrder = index;
        index++;
      });
    });

    return clonedList.filter(p => p.isPopular);
  }

  private sortList(a, b) { return (a.sortOrder < b.sortOrder) ? -1 : (a.sortOrder > b.sortOrder) ? 1 : 0; }
}
